// Phoenix Capital — the one place a URL that came from OUTSIDE this codebase is
// decided to be safe to put in an `href`.
//
// 🔴 WHY THIS EXISTS. Before it, nothing in the frontend sanitised a URL. The
// closest thing, `lib/listingDisplay.ts`, does validate a scheme — but it
// returns a DISPLAY HOST and never an href, so it could not be reused here. The
// 44 hand-rolled external anchors in this app all point at values the app itself
// produced (a detail route, a listing URL captured through a validating server
// path), and none of them faced the case this helper is written for:
// `CalendarAppointment.VideoLink` is free text typed by a staff member OR
// produced by the Calendar AI Create+ parser from arbitrary pasted content, and
// it is length-capped and nothing else on either tier.
//
// A `javascript:` value in that column reaches the DTO unmolested today. Render
// it into an `href` and clicking the meeting's Join button executes it with the
// authenticated user's session.
//
// 🔴 ALLOW-LIST, NEVER A DENY-LIST. Blocking `javascript:` and `data:` invites
// the next reader to wonder about `vbscript:`, `blob:`, `filesystem:` and
// whatever a browser ships next. Two schemes are permitted and everything else —
// including anything unparseable — is refused. A refusal is not an error state:
// the caller renders the value as plain selectable text, so a staff member who
// pasted something odd can still read it, copy it, and fix it.

/** The only schemes that may become a clickable link. */
const ALLOWED_PROTOCOLS = ["http:", "https:"] as const;

/**
 * Returns the URL when it is safe to use as an `href`, or `null` when it is not.
 *
 * 🔴 A `null` RETURN IS NOT AN ERROR — it is the instruction to render the value
 * as text instead of as a link. Callers must handle it; falling back to
 * `href={safeHref(x) ?? x}` would defeat the entire helper.
 *
 * Deliberately does NOT repair input. `lib/listingDisplay.ts` prepends `https://`
 * to a scheme-less string, which is right for a marketplace URL a user typed into
 * a field labelled "listing URL"; it is wrong here, because guessing a scheme for
 * a value the AI parser extracted would turn a fragment of prose into a live
 * outbound link. If it does not already say http or https, it is not a link.
 */
export function safeHref(raw: string | null | undefined): string | null {
  if (!raw) return null;

  const trimmed = raw.trim();
  if (!trimmed) return null;

  // `new URL` is the parser the browser itself uses, so it agrees with what the
  // anchor would do — a hand-rolled regex would not, and the disagreement is
  // exactly where a bypass lives. It also normalises the leading/embedded
  // whitespace and control characters browsers historically stripped before
  // resolving a scheme (`java\nscript:` and friends).
  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    // Unparseable, or relative. Relative is refused on purpose: a video link
    // pointing inside this app is not a video link.
    return null;
  }

  if (!ALLOWED_PROTOCOLS.includes(parsed.protocol as (typeof ALLOWED_PROTOCOLS)[number])) {
    return null;
  }

  // The PARSED form, not the raw one. They differ for exactly the inputs worth
  // worrying about, and returning the raw string would hand the anchor something
  // the check above never saw.
  return parsed.href;
}
